/** Teacher report browser. Read grants without requiring a compound date index. */
import { useEffect, useState } from "react";
import { folder } from "shared/types/Report";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { getHomeworkAssignmentRoot } from "../homeworkAssignmentRoot";
import { parseDate } from "shared/utils/dateUtils";

const useHomework = () => {
    const { getBranchId } = useGetBranchId();
    const { readFirestore, readFirestoreOneDoc } = useFirestore();
    const [selectedMenu, setSelectedMenu] = useState(1);
    const [nowFolder, setNowFolder] = useState("Report");
    const [selectedReport, setSelectedReport] = useState("");
    const [selectedReportFolder, setSelectedReportFolder] = useState("");
    const [folderList, setFolderList] = useState<folder[]>([]);
    const [loadError, setLoadError] = useState("");
    const [reload, setReload] = useState(0);

    useEffect(() => {
        if (selectedMenu !== 1) return;
        let cancelled = false;
        setFolderList([]);
        setLoadError("");
        const load = async () => {
            try {
                let folders: folder[];
                if (nowFolder === "Report") {
                    const branchId = await getBranchId();
                    if (!branchId) throw new Error("학원 정보를 확인하지 못했습니다.");
                    const snapshot = await readFirestore(`ReportAssignBranch/${branchId}/Report`);
                    if (!Array.isArray(snapshot.docs)) throw snapshot;
                    const today = parseDate(new Date());
                    const roots = new Map<string, { path: string; id: string }>();
                    snapshot.docs.forEach((item: { data: () => any }) => {
                        const assignment = item.data();
                        const range = assignment.effective_date;
                        if (!range || range.start > today || range.end < today) return;
                        const root = getHomeworkAssignmentRoot(assignment.report);
                        roots.set(`${root.path}/${root.id}`, root);
                    });
                    const loaded: (folder | null)[] = [];
                    const entries = Array.from(roots.values());
                    for (let offset = 0; offset < entries.length; offset += 10) {
                        loaded.push(...await Promise.all(entries.slice(offset, offset + 10).map(async root => {
                            const report = await readFirestoreOneDoc(root.path, root.id);
                            if (!report.exists()) return null;
                            const data = report.data();
                            return { ...root, name: data.name, type: data.type } as folder;
                        })));
                    }
                    folders = loaded.filter((item): item is folder => item !== null);
                } else {
                    const snapshot = await readFirestore(nowFolder);
                    if (!Array.isArray(snapshot.docs)) throw snapshot;
                    folders = snapshot.docs.map((item: { id: string; data: () => any }) => ({
                        id: item.id, path: nowFolder, name: item.data().name, type: item.data().type,
                    }));
                }
                folders.sort((a, b) => {
                    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
                    return a.name.localeCompare(b.name, "ko", { numeric: true });
                });
                if (!cancelled) setFolderList(folders);
            } catch (error) {
                console.error("배포된 과제 목록 조회 실패:", error);
                if (!cancelled) {
                    setFolderList([]);
                    setLoadError("배포된 과제 목록을 불러오지 못했습니다. 다시 불러오기를 눌러주세요.");
                }
            }
        };
        void load();
        return () => { cancelled = true; };
        // Firestore wrappers are recreated each render; navigation controls refreshes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nowFolder, selectedMenu, reload]);

    const onMenuItemClickHandler = (id: number) => {
        setSelectedMenu(id);
        setNowFolder("Report");
        setSelectedReport("");
        setSelectedReportFolder("");
    };

    const onFolderClick = (item: folder) => {
        if (item.type === "folder") {
            setNowFolder(`${item.path}/${item.id}/Report`);
            setSelectedReport("");
            setSelectedReportFolder("");
        } else {
            setSelectedReport(item.id);
            setSelectedReportFolder(item.path || nowFolder);
        }
    };

    const onPrevClickHandler = () => {
        if (nowFolder === "Report") return;
        const segments = nowFolder.split("/");
        setNowFolder(segments.length > 2 ? segments.slice(0, -2).join("/") : "Report");
        setSelectedReport("");
        setSelectedReportFolder("");
    };

    return {
        selectedMenu, folderList, onMenuItemClickHandler, onFolderClick, nowFolder,
        onPrevClickHandler, selectedReport, reportFolder: selectedReportFolder || nowFolder,
        loadError, retryLoad: () => setReload(value => value + 1),
    };
};

export default useHomework;
